import { Collection } from "intake_shared/src/enum";
import {
    AvailabilityStatus,
    BookingStatus,
    ClientStatus,
    IClient,
    IAppointmentChecks,
    ISessionAttendee,
    IClientBooking,
    IClinician,
    IIntakeAvailability,
    IReservedSchedule,
    RecurrenceRule,
    ServiceDeliveryOption,
    SignatureData,
    ClinicianCredential,
    UpdatedFrom,
    ConsentForm,
    ConsentFormCache,
} from "intake_shared/src/types";
import { v4 as uuidv4 } from "uuid";
import db, { firebaseAuth, fxs } from "../utils/firestore";
import { httpsCallable } from "firebase/functions";
import {
    sendAppointmentConfirmedClinicianSupervisorIntakeClientEmails,
    sendAppointmentConfirmedSupervisorIntakeClientEmails,
    sendAppointmentHeldEmails,
    sendAssignClientEmail,
    sendClinicianReservedWindowEmails,
} from "../utils/queue_email";
import { fetchClientsByEmails, getClientById, saveClientData } from "./client";
import {
    getClinicianByEmail,
    updateClinicianData,
    updateIntakeSessionStatusByBooking,
} from "./clinician";
import { assignIn, cloneDeep } from "lodash";
import { arrayRemove, arrayUnion, serverTimestamp } from "firebase/firestore";
import { calculateFees } from "intake_shared/src/fees";
import { DepartmentCache } from "./cache/requested_service";
import { findLabelByPracticeId, getConfirmedEmailToSend } from "../utils/utils";
import {
    getAppointmentConsentForm,
    getAppointmentConsentFormType,
} from "../utils/consent_form";
import { getAppointmentHeldValidationMessage } from "../utils/guardian";
import {
    getCollateralConsentAppointmentCheck,
    isCollateralConsentService,
} from "../utils/session_attendee_consent";

type ConfirmAppointmentTxnResult = {
    updated_client: IClient;
    updated_clinician: IClinician;
    client_appointment: IClientBooking;
    supervisorDepartment: string;
    form: ConsentForm;
};

function updateClientSelfPayForIntern(
    clinician: IClinician,
    client: IClient,
    updatedBy: string
): IClient {
    if (clinician.credential === ClinicianCredential.INTERN) {
        const updatedClient = { ...client };

        if (!updatedClient[ClientStatus.FEE_SETTING]) {
            updatedClient[ClientStatus.FEE_SETTING] = {
                masters_fee: "",
                dr_fee: "",
                dr_fee_a: "",
                dr_fee_b: "",
                dr_fee_c: "",
                psychiatry_fee: "",
                psychiatry_fee_a: "",
                psychiatry_fee_b: "",
                psychiatry_fee_c: "",
                intern_fee: "",
                lic_fee: "",
                kap_fee: "",
                nutrition_fee: "",
                inn_nutrition_fee: "",
                lic_a_fee: "",
                lic_b_fee: "",
                lic_c_fee: "",
                extern_fee: "",
                inn_masters_fee: "",
                career_fee: "",
                gifted_assessment_fee: "",
                short_term_evaluation_documentation_fee: "",
                testing_fee: "",
                intern_extern_testing_fee: "",
            };
        }

        updatedClient[ClientStatus.FEE_SETTING] = {
            ...updatedClient[ClientStatus.FEE_SETTING]!,
            is_self_pay: true,
        };
        updatedClient.fees_update_details = {
            ...(client.fees_update_details ?? {}),
            updated_by: updatedBy,
            updated_at: new Date(),
        };

        return updatedClient;
    }

    return client;
}

export async function updateBooking(
    clinicianId: string,
    clientId: string,
    appointmentId: string,
    booking_changes: { [key: string]: any },
    appt_checks: { [key: string]: any },
    appointmentState: "held" | "confirmed",
    lastUpdatedBy: string,
    confirmedApptSignature?: SignatureData,
    allDepartmentsAndServices?: DepartmentCache,
    intakeSignature?: SignatureData,
    updated_from: "supervisor_intake_session" | "general_update" = "general_update"
): Promise<IClient | false> {
    try {
        const clinician_doc_ref = db
            .collection(Collection.CLINICIANS)
            .doc(clinicianId);
        const client_doc_ref = db.collection(Collection.CLIENTS).doc(clientId);
        const client_appt_field =
            appointmentState === "held" ? "held_bookings" : "confirmed_bookings";

        const isOwnBooking =
            firebaseAuth.currentUser?.email?.toLowerCase() ===
            clinicianId.toLowerCase();
        const { updated_booking, updated_client, clinician } = isOwnBooking
            ? await (async () => {
                  const updateOwnBooking = httpsCallable(
                      fxs,
                      "httpFunctionUpdateOwnBooking"
                  );
                  await updateOwnBooking({
                      action: "update",
                      clinicianId,
                      clientId,
                      appointmentId,
                      appointmentState,
                      changes: booking_changes,
                      checks:
                          "session_attendees" in booking_changes
                              ? {
                                    collateral_consent_good_form_on_file:
                                        appt_checks.collateral_consent_good_form_on_file,
                                    collateral_consent_rechecked:
                                        appt_checks.collateral_consent_rechecked,
                                    collateral_consent_missing_attendee_count:
                                        appt_checks.collateral_consent_missing_attendee_count,
                                }
                              : appt_checks,
                  });
                  const [clinicianSnap, clientSnap] = await Promise.all([
                      clinician_doc_ref.get(),
                      client_doc_ref.get(),
                  ]);
                  if (!clinicianSnap.exists || !clientSnap.exists) {
                      throw new Error("Cannot find clinician or client");
                  }
                  const clinician = {
                      ...clinicianSnap.data(),
                      id: clinicianId,
                  } as IClinician;
                  const updatedClient = {
                      ...clientSnap.data(),
                      id: clientId,
                  } as IClient;
                  const updatedBooking = clinician[appointmentState]?.find(
                      (entry) => entry.id === appointmentId
                  );
                  if (!updatedBooking) {
                      throw new Error("Cannot find updated booking");
                  }
                  return {
                      updated_booking: updatedBooking,
                      updated_client: updatedClient,
                      clinician,
                  };
              })()
            : await db.runTransaction(async (transaction) => {
                  const clinician_snap = await transaction.get(clinician_doc_ref);
                  const client_snap = await transaction.get(client_doc_ref);
                  if (!clinician_snap.exists || !client_snap.exists) {
                      throw new Error("Cannot find clinician or client");
                  }

                  const clinician = {
                      ...clinician_snap.data(),
                      id: clinicianId,
                  } as IClinician;
                  const client = { ...client_snap.data(), id: clientId } as IClient;

                  const clinicianBookings: IClientBooking[] =
                      clinician[appointmentState] || [];
                  const clientBookings: IClientBooking[] =
                      client[client_appt_field] || [];

                  const booking = clinicianBookings.find(
                      (b) => b.id === appointmentId
                  );
                  if (!booking) {
                      throw new Error(
                          `Booking ${appointmentId} not found on clinician ${clinicianId}`
                      );
                  }
                  const clientBooking = clientBookings.find(
                      (c) => c.id === appointmentId
                  );
                  const updated_booking = {
                      ...booking,
                      ...booking_changes,
                      ...(!("session_attendees" in booking_changes) &&
                          clientBooking?.session_attendees !== undefined && {
                              session_attendees: clientBooking.session_attendees,
                          }),
                  };
                  const mergedApptCheck = {
                      ...((client.appointment_checks || {})[appointmentId] || {}),
                      ...appt_checks,
                  };
                  const appointment_checks = {
                      ...(client.appointment_checks || {}),
                      [appointmentId]: isCollateralConsentService(updated_booking)
                          ? getCollateralConsentAppointmentCheck(
                                mergedApptCheck,
                                updated_booking,
                                updated_booking.session_attendees || []
                            )
                          : mergedApptCheck,
                  };
                  const updated_client: IClient = {
                      ...client,
                      [client_appt_field]: [
                          ...clientBookings.filter((c) => c.id !== appointmentId),
                          updated_booking,
                      ],
                      appointment_checks,
                  };

                  transaction.update(clinician_doc_ref, {
                      [appointmentState]: [
                          ...clinicianBookings.filter((c) => c.id !== appointmentId),
                          updated_booking,
                      ],
                      updated_at: new Date(),
                      updated_from: UpdatedFrom.INTAKE,
                  });
                  transaction.update(client_doc_ref, {
                      [client_appt_field]: updated_client[client_appt_field],
                      appointment_checks,
                      last_updated_by: lastUpdatedBy,
                      date_updated: new Date(),
                      inquiry_method: client.inquiry_method || "",
                      updated_from: UpdatedFrom.INTAKE,
                  });

                  return { updated_booking, updated_client, clinician };
              });

        if ("is_paused" in booking_changes) {
            await updatePausedAppointment(
                clinicianId,
                clientId,
                appointmentId,
                !booking_changes.is_paused
            );
        }
        if (
            updated_booking?.supervisor_intake_session &&
            updated_from === "supervisor_intake_session"
        ) {
            const supervisorDepartment =
                findLabelByPracticeId(
                    updated_booking?.supervisor_intake_session?.practice_id || "",
                    allDepartmentsAndServices || ({} as DepartmentCache)
                ) || "";

            await sendAppointmentConfirmedClinicianSupervisorIntakeClientEmails(
                updated_client,
                clinician,
                updated_booking,
                intakeSignature || ({} as SignatureData),
                supervisorDepartment,
                allDepartmentsAndServices || ({} as DepartmentCache),
                updated_booking?.supervisor_intake_session?.location || ""
            );

            const supervisor_doc = db
                .collection(Collection.CLINICIANS)
                .doc(updated_booking?.supervisor_intake_session?.supervisor_id);

            const supervisor = (await supervisor_doc.get()).data() as IClinician;

            await sendAppointmentConfirmedSupervisorIntakeClientEmails(
                updated_client,
                clinician,
                updated_booking,
                confirmedApptSignature || ({} as SignatureData),
                supervisorDepartment,
                allDepartmentsAndServices || ({} as DepartmentCache),
                updated_booking?.supervisor_intake_session?.location || "",
                supervisor?.contact_info_client || "",
                supervisor?.email || "",
                supervisor?.fullname ||
                    `${supervisor?.firstname || ""} ${supervisor?.lastname || ""}`
            );
        }
        return updated_client;
    } catch (ex) {
        console.log("ex ..", ex);
        return false;
    }
}

export async function moveConfirmedAppointmentToHeld(
    clinicianId: string,
    clientId: string,
    appointmentId: string,
    booking_changes: { [key: string]: any },
    last_updated_by: string
) {
    try {
        const client_doc_ref = db.collection(Collection.CLIENTS).doc(clientId);
        const clinician_doc_ref = db
            .collection(Collection.CLINICIANS)
            .doc(clinicianId);
        await db.runTransaction(async (transaction) => {
            // Check that the client and clincian doc exists.
            const clinician_doc = await transaction.get(clinician_doc_ref);
            const client_doc = await transaction.get(client_doc_ref);
            if (!clinician_doc.exists || !client_doc.exists) {
                throw new Error("Error: Cannot find clinician or client");
            }

            // Check that the appointment is confirmed.
            const clinician = (await clinician_doc.data()) as IClinician;
            const client = (await client_doc.data()) as IClient;
            const heldValidationMessage =
                getAppointmentHeldValidationMessage(client);
            if (heldValidationMessage) {
                throw new Error(heldValidationMessage);
            }
            const client_appointment = client.confirmed_bookings.find(
                (c) => c.id === appointmentId
            );
            const clinician_appointment = clinician.confirmed.find(
                (c) => c.id === appointmentId
            );
            if (!client_appointment || !clinician_appointment) {
                throw new Error(
                    "Error: Appointment not found on client or clinician confirmed."
                );
            }
            const appt_to_move: IClientBooking = assignIn(
                cloneDeep(clinician_appointment),
                client_appointment,
                booking_changes
            );
            appt_to_move.status = BookingStatus.HELD;
            const updated_clinician: IClinician = {
                ...clinician,
                confirmed: clinician.confirmed.filter((c) => c.id !== appointmentId),
            };
            updated_clinician.held.push(appt_to_move);
            const updated_client: IClient = {
                ...client,
                confirmed_bookings: client.confirmed_bookings.filter(
                    (c) => c.id !== appointmentId
                ),
            };
            updated_client.held_bookings.push(appt_to_move);
            updated_client.status = ClientStatus.APPT_HELD;
            updated_client.status_history[updated_client.status] = new Date();
            updated_client.date_updated = new Date();
            transaction.update(clinician_doc_ref, {
                confirmed: updated_clinician.confirmed,
                held: updated_clinician.held,
                last_updated_by,
                updated_from: UpdatedFrom.INTAKE,
            });

            transaction.update(client_doc_ref, {
                held_bookings: updated_client.held_bookings,
                confirmed_bookings: updated_client.confirmed_bookings,
                status: updated_client.status,
                status_history: updated_client.status_history,
                last_updated_by,
                updated_from: UpdatedFrom.INTAKE,
                date_updated: updated_client.date_updated,
            });
        });
        return true;
    } catch (ex) {
        console.error({ error: ex });
        return false;
    }
}

export async function confirmAppointment(
    clinicianId: string,
    clientId: string,
    appointmentId: string,
    last_updated_by: string,
    allDepartmentAndServices: DepartmentCache,
    signature: SignatureData,
    confirmation_type: "client_and_clinician_confirm" | "clinician_confirm_only",
    department: string,
    locationAddress: string,
    allConsentForms: ConsentFormCache,
    appointmentPractice: string,
    intakeSignature: SignatureData
): Promise<boolean> {
    try {
        const tbdAssignmentOfficerEmail = "e.messinger@humantold.com";
        const tbdAssignmentNotes = "Client confirmed with TBD rate";
        const parkHillAssignmentEmail = "brandy@humantold.com";
        const parkHillAssignmentNotes = "Client confirmed as Park Hill client";
        const client_doc_ref = db.collection(Collection.CLIENTS).doc(clientId);
        const clinician_doc_ref = db
            .collection(Collection.CLINICIANS)
            .doc(clinicianId);

        let supervisorIdForIntakeUpdate: string | undefined;

        const txnResult = await db.runTransaction<ConfirmAppointmentTxnResult>(
            async (transaction) => {
                // Check that the client and clincian doc exists.
                const clinician_doc = await transaction.get(clinician_doc_ref);
                const client_doc = await transaction.get(client_doc_ref);
                if (!clinician_doc.exists || !client_doc.exists) {
                    throw new Error("Error: Cannot find clinician or client");
                }

                // Check that the appointment is on held.
                const clinician = (await clinician_doc.data()) as IClinician;
                const client = (await client_doc.data()) as IClient;

                const updatedClient = updateClientSelfPayForIntern(
                    clinician,
                    client,
                    last_updated_by
                );
                const hasTbdFee = Object.values(
                    client[ClientStatus.FEE_SETTING] || {}
                ).some((fee) => {
                    const result = calculateFees(fee);
                    return result && result.tbd_fee !== "";
                });

                const client_appointment = updatedClient.held_bookings.find(
                    (h) => h.id === appointmentId
                );
                const clinician_appointment = clinician.held.find(
                    (h) => h.id === appointmentId
                );
                if (!client_appointment || !clinician_appointment) {
                    throw new Error(
                        "Error: Appointment not found on client or clinician held."
                    );
                }

                const form =
                    getAppointmentConsentForm(
                        appointmentPractice,
                        client_appointment,
                        allConsentForms
                    ) || ({} as ConsentForm);

                const formType = getAppointmentConsentFormType(
                    appointmentPractice,
                    client_appointment
                );

                const appointmentCheck =
                    updatedClient.appointment_checks?.[appointmentId];
                let appointmentChecks: IAppointmentChecks | undefined;
                if (appointmentCheck) {
                    const nextAppointmentCheck = { ...appointmentCheck };
                    let changed = false;

                    if (form.id && !appointmentCheck.consent_form) {
                        nextAppointmentCheck.consent_form = form;
                        changed = true;
                    }

                    if (formType && !appointmentCheck.consent_form_type) {
                        nextAppointmentCheck.consent_form_type = formType;
                        changed = true;
                    }

                    if (changed) {
                        appointmentChecks = {
                            ...updatedClient.appointment_checks,
                            [appointmentId]: nextAppointmentCheck,
                        };
                    }
                }

                // Check that the appointment has not been confirmed for the clinician or marked as available.
                const confirmed_appointment = clinician.confirmed.find(
                    (c) => c.id === appointmentId
                );
                if (confirmed_appointment) {
                    throw new Error(
                        `Error: Appointment is already confirmed for ${confirmed_appointment.client_display_name}`
                    );
                }

                client_appointment.status = BookingStatus.CONFIRMED;
                client_appointment.confirmed_on = new Date().toISOString();

                supervisorIdForIntakeUpdate =
                    client_appointment.supervisor_intake_session?.supervisor_id;

                const supervisorDepartment =
                    findLabelByPracticeId(
                        client_appointment?.supervisor_intake_session?.practice_id ||
                            "",
                        allDepartmentAndServices
                    ) || "";
                if (client_appointment.supervisor_intake_session) {
                    client_appointment.supervisor_intake_session.status =
                        "confirmed" as const;
                }

                const updated_clinician: IClinician = {
                    ...clinician,
                    held: clinician.held.filter((h) => h.id !== appointmentId),
                };
                updated_clinician.confirmed.push(client_appointment);
                // updated_clinician.confirmed = uniqBy(updated_clinician.confirmed, "id");

                const updated_client: IClient = {
                    ...updatedClient,
                    held_bookings: updatedClient.held_bookings.filter(
                        (h) => h.id !== appointmentId
                    ),
                };
                if (appointmentChecks) {
                    updated_client.appointment_checks = appointmentChecks;
                }

                updated_client.confirmed_bookings.push(client_appointment);
                if (hasTbdFee && department !== "PH") {
                    updated_client.secondary_assignee = tbdAssignmentOfficerEmail;
                } else if (department === "PH") {
                    updated_client.secondary_assignee = parkHillAssignmentEmail;
                } else {
                    updated_client.secondary_assignee =
                        updatedClient.secondary_assignee || "";
                }

                // else if (
                //     !hasTbdFee &&
                //     updatedClient.assigned_to === tbdAssignmentOfficerEmail
                // ) {
                //     updated_client.assigned_to = "";
                // }
                // updated_client.confirmed_bookings = uniqBy(updated_client.confirmed_bookings, "id");
                updated_client.status = ClientStatus.APPT_CONFIRMED;
                updated_client.status_history[updated_client.status] = new Date();
                updated_client.date_updated = new Date();

                if (supervisorIdForIntakeUpdate) {
                    try {
                        await updateIntakeSessionStatusByBooking(
                            transaction,
                            supervisorIdForIntakeUpdate,
                            appointmentId,
                            clientId,
                            "confirmed"
                        );
                    } catch (error) {
                        console.error(
                            "Failed to update supervisor intake session status to confirmed",
                            error
                        );
                    }
                }

                transaction.update(clinician_doc_ref, {
                    confirmed: updated_clinician.confirmed,
                    held: updated_clinician.held,
                    last_updated_by,
                    updated_from: UpdatedFrom.INTAKE,
                });

                const clientUpdates: Partial<IClient> = {
                    held_bookings: updated_client.held_bookings,
                    confirmed_bookings: updated_client.confirmed_bookings,
                    status: updated_client.status,
                    status_history: updated_client.status_history,
                    assigned_to: updated_client.assigned_to || "",
                    secondary_assignee: updated_client.secondary_assignee || "",
                    last_updated_by,
                    updated_from: UpdatedFrom.INTAKE,
                    date_updated: updated_client.date_updated,
                    // Include fee setting update if clinician is Intern
                    ...(clinician.credential === ClinicianCredential.INTERN && {
                        [ClientStatus.FEE_SETTING]:
                            updatedClient[ClientStatus.FEE_SETTING],
                        fees_update_details: updatedClient.fees_update_details,
                    }),
                };
                if (appointmentChecks) {
                    clientUpdates.appointment_checks = appointmentChecks;
                }
                transaction.update(client_doc_ref, clientUpdates);

                return {
                    updated_client,
                    updated_clinician,
                    client_appointment,
                    supervisorDepartment,
                    form,
                };
            }
        );

        const {
            updated_client,
            updated_clinician,
            client_appointment,
            supervisorDepartment,
            form,
        } = txnResult;

        let supervisor: IClinician | undefined;
        if (updated_clinician.supervisor_id) {
            supervisor = await getClinicianByEmail(updated_clinician.supervisor_id);
        }
        const supervisor_name = client_appointment?.supervisor_intake_session
            ? client_appointment?.supervisor_intake_session?.clinician_display_name
            : `${supervisor?.firstname || ""} ${supervisor?.lastname || ""}` ||
              `${supervisor?.fullname || ""}` ||
              "";

        getConfirmedEmailToSend(
            confirmation_type,
            updated_client,
            updated_clinician,
            client_appointment,
            allDepartmentAndServices,
            signature,
            supervisor_name ? supervisor_name : "",
            client_appointment.requested_service || "",
            client_appointment.service_delivery_option as string,
            department,
            locationAddress,
            "system",
            form,
            intakeSignature
        );

        updated_client.secondary_assignee === tbdAssignmentOfficerEmail &&
            sendAssignClientEmail(
                updated_client,
                tbdAssignmentOfficerEmail,
                tbdAssignmentNotes,
                intakeSignature
            );

        department === "PH" &&
            updated_client.secondary_assignee === parkHillAssignmentEmail &&
            sendAssignClientEmail(
                updated_client,
                parkHillAssignmentEmail,
                parkHillAssignmentNotes,
                intakeSignature
            );

        if (client_appointment.supervisor_intake_session) {
            const supervisor_intake_session_clinician = await getClinicianByEmail(
                client_appointment.supervisor_intake_session.supervisor_id
            );

            await sendAppointmentConfirmedClinicianSupervisorIntakeClientEmails(
                updated_client,
                updated_clinician,
                client_appointment,
                intakeSignature,
                supervisorDepartment || department,
                allDepartmentAndServices,
                locationAddress
            );

            if (confirmation_type === "client_and_clinician_confirm") {
                await sendAppointmentConfirmedSupervisorIntakeClientEmails(
                    updated_client,
                    updated_clinician,
                    client_appointment,
                    signature,
                    supervisorDepartment || department,
                    allDepartmentAndServices,
                    locationAddress,
                    supervisor_intake_session_clinician?.contact_info_client || "",
                    supervisor_intake_session_clinician?.email || "",
                    supervisor_intake_session_clinician?.fullname ||
                        `${supervisor_intake_session_clinician?.firstname || ""} ${
                            supervisor_intake_session_clinician?.lastname || ""
                        }`
                );
            }
        }
        return true;
    } catch (ex) {
        console.error({ error: ex });
        return false;
    }
}

export async function deleteConfirmedAppointmentFromClinician({
    clinicianId,
    appointmentId,
}: {
    clinicianId: string;
    appointmentId: string;
}) {
    if (
        firebaseAuth.currentUser?.email?.toLowerCase() === clinicianId.toLowerCase()
    ) {
        const clinician = await getClinicianByEmail(clinicianId);
        const booking = clinician?.confirmed.find(
            (entry) => entry.id === appointmentId
        );
        if (!booking) {
            throw new Error("Cannot find appointment");
        }
        const deleteOwnBooking = httpsCallable(fxs, "httpFunctionUpdateOwnBooking");
        await deleteOwnBooking({
            action: "delete_orphan",
            clinicianId,
            clientId: booking.client_id,
            appointmentId,
            appointmentState: "confirmed",
        });
        return;
    }
    const clinician = await getClinicianByEmail(clinicianId);
    if (!clinician) {
        throw new Error("Cannot find clinician");
    }
    clinician.confirmed = clinician.confirmed.filter((c) => c.id !== appointmentId);
    await db.collection(Collection.CLINICIANS).doc(clinicianId).update({
        confirmed: clinician.confirmed,
        updated_from: UpdatedFrom.INTAKE,
    });
}

export async function cancelOwnConfirmedAppointment({
    clinicianId,
    clientId,
    appointmentId,
}: {
    clinicianId: string;
    clientId: string;
    appointmentId: string;
}) {
    const cancelOwnBooking = httpsCallable(fxs, "httpFunctionUpdateOwnBooking");
    await cancelOwnBooking({
        action: "cancel",
        clinicianId,
        clientId,
        appointmentId,
        appointmentState: "confirmed",
    });
}

export async function deleteAppoinment({
    clinicianId,
    clientId,
    appointmentId,
    cancelledBy,
    status = BookingStatus.HELD,
    cancellationNotes = "",
    lastUpdatedBy,
}: {
    clinicianId: string;
    clientId: string;
    appointmentId: string;
    cancelledBy: string;
    lastUpdatedBy: string;
    status?: BookingStatus;
    cancellationNotes?: string;
}): Promise<boolean> {
    const clinician = await getClinicianByEmail(clinicianId);
    const client = await getClientById(clientId);
    if (!clinician || !client) {
        return false;
    }
    switch (status) {
        case BookingStatus.HELD: {
            const appointment = clinician.held.find((h) => h.id === appointmentId);
            if (appointment) {
                appointment.status = AvailabilityStatus.AVAILABLE as any;
                appointment.held_on = null;
                client.appointment_history.push({
                    ...appointment,
                    cancellation_notes: cancellationNotes,
                    cancelled_on: new Date().toISOString(),
                    cancelled_by: cancelledBy,
                    consent_cc_notes:
                        client.appointment_checks?.[appointmentId]
                            ?.consent_cc_notes ?? "",
                });
                const updated_clinician: IClinician = {
                    ...clinician,
                    held: clinician.held.filter((h) => h.id !== appointmentId),
                };
                const availableAppointment = {
                    ...appointment,
                    supervisor_intake_session: null,
                };
                updated_clinician.available.push(availableAppointment);
                await updateClinicianData({
                    clinician_id: updated_clinician.id!,
                    available_schedules: updated_clinician.available,
                    held_bookings: updated_clinician.held,
                });
            }
            const updated_client: IClient = {
                ...client,
                held_bookings: client.held_bookings.filter(
                    (h) => h.id !== appointmentId
                ),
            };
            await saveClientData(updated_client, lastUpdatedBy);
            return true;
        }
        case BookingStatus.CONFIRMED: {
            break;
        }
    }
    return false;
}

export async function fetchReservedAppointments(
    clientEmail: string
): Promise<IReservedSchedule[]> {
    const raw_data = (
        await (
            await db
                .collection(Collection.RESERVED_APPOINTMENTS)
                .where("reservedFor", "==", clientEmail.toLowerCase())
        ).get()
    ).docs;
    return raw_data.map((r) => r.data()) as IReservedSchedule[];
}

export async function reserveAppointment(availability: IReservedSchedule) {
    await db
        .collection(Collection.RESERVED_APPOINTMENTS)
        .doc(availability.id)
        .set(availability);
    await sendClinicianReservedWindowEmails(
        availability.clinicianName as string,
        availability.reservedFor as string,
        availability
    );
}

export async function deleteReservedAppointment(availability: IReservedSchedule) {
    const author = firebaseAuth.currentUser?.email;
    if (!author) {
        throw new Error("Sign in before removing a reservation.");
    }
    if (!availability.id) {
        throw new Error("Select a reservation to remove.");
    }
    const reservationRef = db
        .collection(Collection.RESERVED_APPOINTMENTS)
        .doc(availability.id);
    const logRef = db
        .collection(Collection.RESERVED_APPOINTMENT_LOG)
        .doc(availability.id);
    const clients = await fetchClientsByEmails([availability.reservedFor ?? ""]);

    await db.runTransaction(async (transaction) => {
        const snapshot = await transaction.get(reservationRef);
        if (!snapshot.exists) {
            return;
        }
        const reservation = snapshot.data() as IReservedSchedule;
        if (reservation.reservedFor !== availability.reservedFor) {
            throw new Error("Reservation changed. Refresh before removing it.");
        }
        transaction.set(logRef, {
            timestamp: serverTimestamp(),
            author,
            activity: "deleted",
            reason: "manual_removal",
            reservation_id: snapshot.id,
            clinician_id: reservation.clinicianId ?? null,
            client_id: clients.length === 1 ? clients[0].id : null,
            start: reservation.start ?? null,
            end: reservation.end ?? null,
            recurrence: reservation.recurrenceRule ?? null,
        });
        transaction.delete(reservationRef);
    });
}

export async function holdAppointment({
    clientId,
    clinicianId,
    availableWindowId,
    startingDate,
    clientRequestedCall,
    requestedService,
    clientConcerns,
    clientCallNotes,
    serviceDeliveryOption,
    last_updated_by,
    practice_id,
    department,
    supervisorIntakePayload,
    appointmentChecksUpdate,
    sessionAttendees,
    signature,
}: {
    clientId: string;
    clinicianId: string;
    availableWindowId: string;
    startingDate: Date;
    clientRequestedCall: boolean;
    requestedService: string;
    clientConcerns: string;
    clientCallNotes: string;
    serviceDeliveryOption: ServiceDeliveryOption;
    last_updated_by: string;
    practice_id?: string;
    department?: string;
    supervisorIntakePayload?: {
        selectedSlot: IIntakeAvailability & { supervisorId: string };
        supervisorsWithIntake: IClinician[];
    };
    appointmentChecksUpdate?: Partial<IAppointmentChecks[string]>;
    sessionAttendees?: ISessionAttendee[];
    signature: SignatureData;
}): Promise<boolean> {
    const client_doc_ref = db.collection(Collection.CLIENTS).doc(clientId);
    const clinician_doc_ref = db.collection(Collection.CLINICIANS).doc(clinicianId);
    const reserved_appt_ref = db
        .collection(Collection.RESERVED_APPOINTMENTS)
        .doc(availableWindowId);

    try {
        await db.runTransaction(async (transaction) => {
            // Check that the client and clincian doc exists.
            const clinician_doc = await transaction.get(clinician_doc_ref);
            const client_doc = await transaction.get(client_doc_ref);
            if (!clinician_doc.exists || !client_doc.exists) {
                throw new Error("Error: Cannot find clinician or client");
            }

            // Get the available appointment
            const clinician = (await clinician_doc.data()) as IClinician;
            const client = (await client_doc.data()) as IClient;
            const heldValidationMessage =
                getAppointmentHeldValidationMessage(client);
            if (heldValidationMessage) {
                throw new Error(heldValidationMessage);
            }

            const available_window = clinician.available.find(
                (a) => a.id === availableWindowId
            );
            if (!available_window) {
                throw new Error("Error: Appointment is not available");
            }
            const reservedAppointment = available_window.reservedFor
                ? await transaction.get(reserved_appt_ref)
                : undefined;
            const reservationAuthor = firebaseAuth.currentUser?.email;
            if (reservedAppointment?.exists && !reservationAuthor) {
                throw new Error("Sign in before holding a reserved appointment.");
            }

            // Check that the appointment is not held or confirmed.
            const confirmed_or_held_appt = [
                ...clinician.confirmed,
                ...clinician.held,
            ].find((h) => h.id === availableWindowId);
            if (confirmed_or_held_appt) {
                throw new Error(
                    `Error: Appointment is already held or confirmed for ${confirmed_or_held_appt.client_display_name}`
                );
            }

            const client_booking: IClientBooking = {
                ...available_window,
                client_display_name: `${client.first_name} ${client.last_name[0]}.`,
                clinician_display_name:
                    `${clinician.firstname} ${clinician.lastname}` ||
                    `${clinician.fullname}`,
                client_id: clientId,
                clinician_id: clinicianId,
                status: BookingStatus.HELD,
                requested_service: requestedService,
                starting_date: startingDate.toISOString(),
                service_delivery_option: serviceDeliveryOption,
                held_on: new Date().toISOString(),
                supervisor_intake_session: null,
                practice_id:
                    practice_id ||
                    (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
                department,
                ...(sessionAttendees !== undefined && {
                    session_attendees: sessionAttendees,
                }),
            };
            if (supervisorIntakePayload?.selectedSlot) {
                const selectedIntakeSlot = supervisorIntakePayload.selectedSlot;
                const selectedSupervisor =
                    supervisorIntakePayload.supervisorsWithIntake.find(
                        (sup) =>
                            sup.email === selectedIntakeSlot.supervisorId ||
                            sup.id === selectedIntakeSlot.supervisorId
                    );
                const resolvedIntakePracticeId =
                    selectedIntakeSlot.practice_id ||
                    practice_id ||
                    (process.env.REACT_APP_MANTLE_PRACTICE_ID as string);

                client_booking.supervisor_intake_session = {
                    id: selectedIntakeSlot.id,
                    supervisor_id: selectedIntakeSlot.supervisorId,
                    service_delivery_option:
                        selectedIntakeSlot.service_delivery_option,
                    start_time: selectedIntakeSlot.start_time,
                    end_time: selectedIntakeSlot.end_time,
                    recurrence: RecurrenceRule.ONE_OFF as RecurrenceRule.ONE_OFF,
                    created_at: selectedIntakeSlot.created_at || new Date(),
                    location: selectedIntakeSlot.location || "",
                    office: selectedIntakeSlot.office || "",
                    status: "held",
                    completed_at: null,
                    practice_id: resolvedIntakePracticeId,
                    clinician_display_name:
                        selectedSupervisor?.fullname ||
                        selectedIntakeSlot.clinician_display_name ||
                        "",
                    supervisees_display_name:
                        client_booking.clinician_display_name ||
                        `${clinician.firstname} ${clinician.lastname}`,
                };
            }
            const consentFormType = getAppointmentConsentFormType(
                client_booking.department || "",
                client_booking
            );
            const appointmentChecks = client.appointment_checks || {};
            client.appointment_checks = {
                ...appointmentChecks,
                [client_booking.id]: {
                    ...(isCollateralConsentService(client_booking)
                        ? getCollateralConsentAppointmentCheck(
                              undefined,
                              client_booking,
                              client_booking.session_attendees || []
                          )
                        : {
                              date_added: new Date(),
                              client_requested_call: clientRequestedCall,
                              consult_call_notes: clientConcerns,
                          }),
                    client_requested_call: clientRequestedCall,
                    consult_call_notes: clientConcerns,
                    consent_cc_notes: "",
                    vaccination_checks_completed_on:
                        Object.values(appointmentChecks).find(
                            (c) => c.vaccination_checks_completed_on
                        )?.vaccination_checks_completed_on || null,
                    ...(consentFormType
                        ? { consent_form_type: consentFormType }
                        : {}),
                    ...(appointmentChecksUpdate || {}),
                },
            };
            client[ClientStatus.APPT_SETTING] = {
                ...(client[ClientStatus.APPT_SETTING] || {}),
                call_notes: clientCallNotes,
            };
            client.held_bookings.push(client_booking);
            client.status = ClientStatus.APPT_HELD;
            client.status_history[client.status] = new Date();
            client.date_updated = new Date();
            clinician.held.push(client_booking);
            clinician.available = clinician.available.filter(
                (a) => a.id !== available_window.id
            );

            transaction.update(client_doc_ref, {
                status: client.status,
                held_bookings: client.held_bookings,
                appointment_checks: client.appointment_checks,
                [ClientStatus.APPT_SETTING]: client[ClientStatus.APPT_SETTING],
                [ClientStatus.APPT_HELD]: client[ClientStatus.APPT_HELD] || {},
                status_history: client.status_history,
                last_updated_by,
                updated_from: UpdatedFrom.INTAKE,
                date_updated: client.date_updated,
            });

            transaction.update(clinician_doc_ref, {
                held: clinician.held,
                available: clinician.available,
                last_updated_by,
                updated_from: UpdatedFrom.INTAKE,
            });

            if (reservedAppointment?.exists) {
                const reservation = reservedAppointment.data() as IReservedSchedule;
                transaction.set(
                    db
                        .collection(Collection.RESERVED_APPOINTMENT_LOG)
                        .doc(availableWindowId),
                    {
                        timestamp: serverTimestamp(),
                        author: reservationAuthor,
                        activity: "deleted",
                        reason: "appointment_held",
                        reservation_id: availableWindowId,
                        clinician_id: reservation.clinicianId ?? null,
                        client_id: clientId,
                        start: reservation.start ?? null,
                        end: reservation.end ?? null,
                        recurrence: reservation.recurrenceRule ?? null,
                    }
                );
                transaction.delete(reserved_appt_ref);
            }

            sendAppointmentHeldEmails(
                client,
                clinician,
                client_booking,
                clientRequestedCall,
                signature
            );
        });
        return true;
    } catch (ex) {
        console.error({ error: ex });
        return false;
    }
}

export async function addCaseloadBooking(
    client_id: string,
    clinician_email: string,
    booking: IClientBooking,
    last_updated_by: string
) {
    if (
        firebaseAuth.currentUser?.email?.toLowerCase() ===
        clinician_email.toLowerCase()
    ) {
        const addOwnBooking = httpsCallable(fxs, "httpFunctionUpdateOwnBooking");
        await addOwnBooking({
            action: "add",
            clinicianId: clinician_email,
            clientId: client_id,
            appointmentId: booking.id,
            appointmentState: "confirmed",
            changes: {
                start: booking.start,
                end: booking.end,
                starting_date: booking.starting_date,
                recurrenceRule: booking.recurrenceRule,
                ending_date: booking.ending_date ?? "",
            },
        });
        return;
    }
    booking.id = uuidv4();
    booking.created_by_clinician = true;
    booking.status = BookingStatus.CONFIRMED;
    const clinicianRef = db.collection(Collection.CLINICIANS).doc(clinician_email);
    const clientRef = db.collection(Collection.CLIENTS).doc(client_id);
    await db.runTransaction(async (transaction) => {
        const clinicianSnapshot = await transaction.get(clinicianRef);
        const clientSnapshot = await transaction.get(clientRef);
        if (!clientSnapshot.exists || !clinicianSnapshot.exists) {
            throw new Error(
                `No client or clincian with ID. Client ID: ${client_id}, Clinician ID: ${clinician_email}`
            );
        }

        const client = clientSnapshot.data() as IClient;
        const clinician = clinicianSnapshot.data() as IClinician;
        const updatedClient = updateClientSelfPayForIntern(
            clinician,
            client,
            last_updated_by
        );

        transaction.update(clinicianRef, {
            confirmed: [...(clinician.confirmed ?? []), booking],
            last_updated_by,
            updated_from: UpdatedFrom.INTAKE,
        });
        transaction.update(clientRef, {
            confirmed_bookings: [...(client.confirmed_bookings ?? []), booking],
            ...(clinician.credential === ClinicianCredential.INTERN && {
                [ClientStatus.FEE_SETTING]: updatedClient[ClientStatus.FEE_SETTING],
                fees_update_details: updatedClient.fees_update_details,
            }),
            last_updated_by,
            updated_from: UpdatedFrom.INTAKE,
            date_updated: new Date(),
        });
    });
}

async function updatePausedAppointment(
    clinician_id: string,
    client_id: string,
    appointment_id: string,
    remove: boolean
) {
    const pausedApptDoc: { [key: string]: any } = {
        paused: remove ? arrayRemove(appointment_id) : arrayUnion(appointment_id),
    };
    if (!remove) {
        pausedApptDoc.date_paused = {
            [appointment_id]: new Date().toISOString(),
        };
        pausedApptDoc.client_id = {
            [appointment_id]: client_id,
        };
    }
    await db
        .collection(Collection.PAUSED_APPOINTMENTS)
        .doc(clinician_id)
        .set(pausedApptDoc, { merge: true });
}
